// @flow
import 'isomorphic-fetch'
import { Config } from '../util/config'
import { logger } from './logger'
import { observeStore } from './redux'
import { userAuthTokenSelector } from '../ducks/user/selectors'

/**
 * App-related utility functions
 * @module util/app
 */

// Types/Constants ////////////////////////////////////////////////////////////

/** Date V3 was released */
export const V3_RELEASE_DATE: '2018-03-01' = '2018-03-01'

// Environment ////////////////////////////////////////////////////////////////

export type Environment = 'DEVELOPMENT' | 'PRODUCTION'

/**
 * Whether or not this is a dev environment
 * @returns {boolean} - Whether or not this is a dev environment
 */
export const isDev = () => Config.ENVIRONMENT === 'DEVELOPMENT'

// User ///////////////////////////////////////////////////////////////////////

/**
 * When a user logs out, redirect them to the login screen
 * @param  {object} Store - Redux store
 * @returns {function} - Cancel listener function
 */
export const watchForLogout = (Store: Object) => {
  return observeStore(Store, userAuthTokenSelector, (newToken, oldToken) => {
    /** Login path */
    const LOGIN_PATH: '/login' = '/login'
    /** Reset password path */
    const RESET_PASSWORD_PATH: '/reset-forgot-password' =
      '/reset-forgot-password'

    if (oldToken && typeof oldToken === 'string' && !newToken) {
      // Make sure we're not triggering unnecessary redirects/updates
      if (
        !window.location.href.includes(LOGIN_PATH) &&
        !window.location.href.includes(RESET_PASSWORD_PATH) &&
        !isLimitedAccess()
      ) {
        logger.info(`No user token; sending to ${LOGIN_PATH}`, {
          store: Store.getState()
        })
        window.location.href = LOGIN_PATH
      }
    }
  })
}

// Limited Access /////////////////////////////////////////////////////////////

/**
 * Determine if this is limited-access
 * @param {string} [url] - URL to check if it's limited access; if nothing passed, use current URL
 * @return {boolean} - Whether or not this is a limited access session
 */
export const isLimitedAccess = (url?: string) =>
  (url || window.location.pathname).includes('/limited-access')

// MQTT/Events ////////////////////////////////////////////////////////////////

type EventHandler = (Object, Object) => Promise<any>
type EventReceiver = (string, any) => Object
type EventConnection = (
  url: string,
  env: Environment,
  userId: string,
  eventReceiver: EventReceiver
) => Object

export const createEventListener = (
  userId: ?string,
  store: Object,
  eventHandler: EventHandler,
  eventConnection: EventConnection,
  onError: Error => void
): Promise<*> => {
  return fetch('/mqttURL')
    .then(resp => resp.text())
    .then(URL => {
      let mqttClient

      return (userId: string) => {
        if (userId) {
          mqttClient = eventConnection(
            URL,
            Config.ENVIRONMENT,
            userId,
            (topic, payload) =>
              eventHandler(store, JSON.parse(payload.toString()))
          )
          mqttClient.on('error', onError)
        } else if (mqttClient) {
          mqttClient.end()
          mqttClient = null
        }
      }
    })
}
